import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import SavedPromptChips from 'src/components/SupportCase/SavedPromptChips';
import SaveNudge, {
  isDismissed,
  rememberDismissal,
} from 'src/components/SupportCase/SaveNudge';
import useSavedPrompts from 'src/components/SupportCase/useSavedPrompts';
import type {
  SavedPrompt,
  SavePromptOffer,
} from 'src/components/SupportCase/types';

interface PromptFooterProps {
  /**
   * Run a saved prompt as the first message. Omitted inside an existing case, where a
   * chip would silently start a second conversation — the chips belong to the two
   * surfaces that are already composing a *new* case (the panel and the hub tab).
   */
  onRun?: (prompt: SavedPrompt) => void;
  /** The server's offer to save the question just asked. */
  offer?: SavePromptOffer | null;
  /** Called once the offer has been saved or dismissed. */
  onOfferDone?: () => void;
  disabled?: boolean;
}

/**
 * Everything that lives under the composer: the saved-prompt chips and the one-time
 * offer to save a question (ENG-4220).
 *
 * One component for all three surfaces so a save from the panel updates the chips
 * everywhere on the next fetch, and so the offer's copy, dismissal key and cap
 * handling exist once.
 */
const PromptFooter: FC<PromptFooterProps> = ({
  onRun,
  offer = null,
  onOfferDone,
  disabled = false,
}) => {
  const { prompts, save } = useSavedPrompts();
  // Once the offer is saved or dismissed it must not come back when the parent
  // re-renders with the same response — the parent has no reason to know.
  const [handledKey, setHandledKey] = useState<string | null>(null);

  const showOffer = useMemo(
    () =>
      Boolean(
        offer && offer.body_key !== handledKey && !isDismissed(offer.body_key)
      ),
    [offer, handledKey]
  );

  const handleSave = useCallback(
    async (title: string) => {
      if (!offer) {
        return { ok: false };
      }
      return save(offer.body, title);
    },
    [offer, save]
  );

  const chips = onRun ? (
    <SavedPromptChips prompts={prompts} onRun={onRun} disabled={disabled} />
  ) : null;

  return (
    <>
      {chips}
      {showOffer && offer ? (
        <SaveNudge
          offer={offer}
          onSave={handleSave}
          onDone={() => {
            // Remembered on a save as well as a dismissal: a stale router state would
            // otherwise re-offer a prompt that already exists.
            rememberDismissal(offer.body_key);
            setHandledKey(offer.body_key);
            onOfferDone?.();
          }}
        />
      ) : null}
    </>
  );
};

export default PromptFooter;
